import { z } from 'zod';

/**
 * Document Intelligence DTOs
 *
 * Zod schemas and TypeScript types for document intelligence API.
 */

// ========================================
// ENUMS (as Zod schemas)
// ========================================

export const FindingCategorySchema = z.enum(['health', 'compliance', 'redFlag', 'suggestion', 'risk']);

export const FindingSeveritySchema = z.enum(['critical', 'high', 'medium', 'low', 'info']);

export const FindingStatusSchema = z.enum(['active', 'resolved', 'dismissed', 'false_positive']);

// ========================================
// CITATION
// ========================================

export const CitationSchema = z.object({
  chunkId: z.string().uuid(),
  text: z.string(),
  pageNumber: z.number().int().positive().optional(),
  sectionTitle: z.string().optional(),
  startOffset: z.number().int().nonnegative().optional(),
  endOffset: z.number().int().nonnegative().optional(),
  confidence: z.number().min(0).max(1),
  relevance: z.number().min(0).max(1).optional(),
});

export type Citation = z.infer<typeof CitationSchema>;

// ========================================
// RULE DEFINITION
// ========================================

export const RuleExtractionFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(['string', 'number', 'boolean', 'array', 'object', 'date']),
  description: z.string(),
  required: z.boolean().optional(),
  pattern: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  enum: z.array(z.string()).optional(),
});

export type RuleExtractionField = z.infer<typeof RuleExtractionFieldSchema>;

export const RuleExtractionSchemaSchema = z.object({
  fields: z.array(RuleExtractionFieldSchema),
  instructions: z.string().optional(),
});

export type RuleExtractionSchemaType = z.infer<typeof RuleExtractionSchemaSchema>;

export const RuleSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  category: FindingCategorySchema,
  severity: FindingSeveritySchema,
  prompt: z.string().min(1),
  extractionSchema: RuleExtractionSchemaSchema.optional(),
  enabled: z.boolean().default(true),
  minConfidence: z.number().min(0).max(1).default(0.5),
  tags: z.array(z.string()).optional(),
});

export type Rule = z.infer<typeof RuleSchema>;

// ========================================
// DOCUMENT RULESET
// ========================================

export const DocumentRulesetSettingsSchema = z.object({
  model: z.string().optional(),
  temperature: z.number().min(0).max(2).optional(),
  maxTokens: z.number().int().positive().optional(),
  maxChunksPerRule: z.number().int().positive().default(10),
  parallelRules: z.boolean().default(false),
  maxConcurrentRules: z.number().int().positive().default(3),
});

export type DocumentRulesetSettings = z.infer<typeof DocumentRulesetSettingsSchema>;

export const DocumentRulesetSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  version: z.string().default('1.0'),
  rules: z.array(RuleSchema),
  active: z.boolean().default(true),
  autoRun: z.boolean().default(true),
  settings: DocumentRulesetSettingsSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type DocumentRuleset = z.infer<typeof DocumentRulesetSchema>;

// ========================================
// CREATE RULESET DTO
// ========================================

export const CreateRulesetRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  version: z.string().default('1.0'),
  rules: z.array(RuleSchema).min(1),
  active: z.boolean().default(true),
  autoRun: z.boolean().default(true),
  settings: DocumentRulesetSettingsSchema.optional(),
});

export type CreateRulesetRequest = z.infer<typeof CreateRulesetRequestSchema>;

export const CreateRulesetSchema = CreateRulesetRequestSchema.extend({
  businessId: z.string().uuid(),
});

export type CreateRuleset = z.infer<typeof CreateRulesetSchema>;

// ========================================
// UPDATE RULESET DTO
// ========================================

export const UpdateRulesetRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().optional(),
  version: z.string().optional(),
  rules: z.array(RuleSchema).optional(),
  active: z.boolean().optional(),
  autoRun: z.boolean().optional(),
  settings: DocumentRulesetSettingsSchema.optional(),
});

export type UpdateRulesetRequest = z.infer<typeof UpdateRulesetRequestSchema>;

// ========================================
// DOCUMENT FINDING
// ========================================

export const DocumentFindingSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  businessId: z.string().uuid(),
  rulesetId: z.string().uuid(),
  ruleId: z.string(),
  category: FindingCategorySchema,
  severity: FindingSeveritySchema,
  title: z.string(),
  description: z.string(),
  citations: z.array(CitationSchema),
  confidence: z.number().min(0).max(1),
  status: FindingStatusSchema,
  extractedData: z.record(z.string(), z.any()).optional(),
  userFeedback: z.string().optional(),
  resolvedBy: z.string().uuid().optional(),
  resolvedAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type DocumentFinding = z.infer<typeof DocumentFindingSchema>;

// ========================================
// UPDATE FINDING DTO
// ========================================

export const UpdateFindingRequestSchema = z.object({
  status: FindingStatusSchema.optional(),
  userFeedback: z.string().optional(),
});

export type UpdateFindingRequest = z.infer<typeof UpdateFindingRequestSchema>;

// ========================================
// ANALYZE DOCUMENT REQUEST
// ========================================

export const AnalyzeDocumentRequestSchema = z.object({
  documentId: z.string().uuid(),
  rulesetId: z.string().uuid().optional(), // If not provided, uses default active rulesets
  ruleIds: z.array(z.string()).optional(), // If provided, runs only specific rules
});

export type AnalyzeDocumentRequest = z.infer<typeof AnalyzeDocumentRequestSchema>;

// ========================================
// ANALYSIS RESULT
// ========================================

export const RuleExecutionResultSchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  findings: z.array(DocumentFindingSchema.omit({ id: true, createdAt: true, updatedAt: true })),
  executionTime: z.number().int().nonnegative(),
  error: z.string().optional(),
});

export type RuleExecutionResult = z.infer<typeof RuleExecutionResultSchema>;

export const DocumentAnalysisResultSchema = z.object({
  documentId: z.string().uuid(),
  rulesetId: z.string().uuid(),
  rulesetName: z.string(),
  totalFindings: z.number().int().nonnegative(),
  findingsByCategory: z.record(z.string(), z.number().int().nonnegative()),
  findingsBySeverity: z.record(z.string(), z.number().int().nonnegative()),
  ruleResults: z.array(RuleExecutionResultSchema),
  startedAt: z.date(),
  completedAt: z.date(),
  totalExecutionTime: z.number().int().nonnegative(),
});

export type DocumentAnalysisResult = z.infer<typeof DocumentAnalysisResultSchema>;

// ========================================
// FINDING SUMMARY
// ========================================

export const FindingSummarySchema = z.object({
  documentId: z.string().uuid(),
  documentName: z.string(),
  totalFindings: z.number().int().nonnegative(),
  criticalCount: z.number().int().nonnegative(),
  highCount: z.number().int().nonnegative(),
  mediumCount: z.number().int().nonnegative(),
  lowCount: z.number().int().nonnegative(),
  findingsByCategory: z.record(z.string(), z.number().int().nonnegative()),
  lastAnalyzedAt: z.date().optional(),
});

export type FindingSummary = z.infer<typeof FindingSummarySchema>;

// ========================================
// QUERY/FILTER DTOs
// ========================================

export const FindingsFilterSchema = z.object({
  documentId: z.string().uuid().optional(),
  rulesetId: z.string().uuid().optional(),
  category: FindingCategorySchema.optional(),
  severity: FindingSeveritySchema.optional(),
  status: FindingStatusSchema.optional(),
  minConfidence: z.number().min(0).max(1).optional(),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sortBy: z.enum(['createdAt', 'severity', 'confidence']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type FindingsFilter = z.infer<typeof FindingsFilterSchema>;

export const RulesetsFilterSchema = z.object({
  active: z.boolean().optional(),
  autoRun: z.boolean().optional(),
  search: z.string().optional(), // Search by name/description
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sortBy: z.enum(['createdAt', 'name', 'updatedAt']).default('updatedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type RulesetsFilter = z.infer<typeof RulesetsFilterSchema>;
